import { useEffect, useState } from "react";
import { customer_agreement_get_customer_report } from "../../../API/api";
import customFetch from "../../../API/CustomFetch";
import showToastMessage from "../../../Toastify/Index";
import {
  CustomerInspectionReportResponse,
  InspectionItem,
  VehicleInspectionData,
} from "./type/datatypes";
import PlainRow from "../common/PlainRow";
import LeadCardSkeleton from "../../../layouts/spinners/LeadCardSkeleton";

type Props = {
  vehicleProfileId: string;
};

const VehiclePersonalInfoSection = ({
  data = [],
}: {
  data?: InspectionItem[];
}) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="font-medium text-black rounded-t-lg mx-2">
        Vehicle & Personal Information
      </div>

      <div className="p-3 md:p-4 grid grid-cols-1 md:grid-cols-2 gap-3 gap-x-16 gap-y-2 border rounded-lg m-2">
        {data.map((item, index) => (
          <PlainRow
            key={index}
            label={item.Question}
            value={item.Response || "N/A"}
          />
        ))}
      </div>
    </div>
  );
};
/* ---------------- Reusable Section ---------------- */
const InspectionSection = ({
  title,
  data,
}: {
  title: string;
  data?: InspectionItem[] | null;
}) => {
  if (!data || data.length === 0) return null;

  const mid = Math.ceil(data.length / 2);
  const left = data.slice(0, mid);
  const right = data.slice(mid);

  const gridCols = "2fr 1fr 2fr 1fr";

  return (
    <div className="bg-white p-4 border rounded-lg">
      {/* TITLE */}
      <div className="font-medium text-black mx-2">{title}</div>

      {/* INNER BOX */}
      <div className="border rounded-lg m-2">
        {/* HEADER */}
        <div
          className="hidden md:grid bg-white text-[#00AA13] text-sm font-medium p-4 gap-6"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div>List of Items</div>
          <div className="text-right">Response</div>
          <div className="pl-4">List of Items</div>
          <div className="text-right">Response</div>
        </div>

        {/* ROWS */}
        <div className="bg-white  md:hidden">
          <div className="space-y-3 rounded-lg bg-[#F4F7F4] p-3">
            {data.map((item, index) => (
              <div key={index} className="">
                <div className="flex items-start gap-2 text-black font-medium">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 shrink-0"></span>
                  <span>{item.Question}</span>
                </div>
                <div className="mt-1 pl-4 text-black break-words">
                  {item.Response || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden md:block space-y-3 text-sm bg-[#F4F7F4] p-4">
          {Array.from({ length: mid }).map((_, i) => (
            <div
              key={i}
              className="grid items-center"
              style={{ gridTemplateColumns: gridCols }}
            >
              {/* LEFT */}
              <div className="flex items-center gap-2 text-black">
                <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                {left[i]?.Question}
              </div>
              <div className="text-right text-black">
                {left[i]?.Response || "-"}
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-2 text-right pl-8">
                {right[i] ? (
                  <>
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    {right[i]?.Question}
                  </>
                ) : null}
              </div>
              <div className="text-right text-black ">
                {right[i]?.Response || (right[i] ? "-" : "")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
/* ---------------- Main Component ---------------- */

const CustomerInspectionReport = ({ vehicleProfileId }: Props) => {
  const [data, setData] = useState<VehicleInspectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("");

  const fetchReport = async () => {
    try {
      setLoading(true);
      const url = customer_agreement_get_customer_report(
        vehicleProfileId,
        "53",
      );

      const res: CustomerInspectionReportResponse = await customFetch(url);

      if (res?.SUCCESS) {
        setData(res.DATA.vehicleData[0]);
      } else {
        showToastMessage(false, "Failed to fetch report");
      }
    } catch {
      showToastMessage(false, "Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicleProfileId) fetchReport();
  }, [vehicleProfileId]);

  useEffect(() => {
    if (previewImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // cleanup (important)
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [previewImage]);

  return (
    <div>
      {loading ? (
        <LeadCardSkeleton />
      ) : (
        <div>
          <div className="space-y-6">
            {/* Vehicle & Personal Info */}
            {/* <InspectionSection
        title="Vehicle & Personal Information"
        data={data?.["Vehicle&PersonalInfo"]}
      /> */}

            <VehiclePersonalInfoSection data={data?.["Vehicle&PersonalInfo"]} />

            <InspectionSection
              title="Wheel, Tyre and Brakes"
              data={data?.WheelTyreBrakes}
            />

            <InspectionSection title="Switches" data={data?.Switches || []} />

            <InspectionSection
              title="Lights and Indicators"
              data={data?.indicatorAndLights}
            />

            <InspectionSection
              title="Displays and Seat"
              data={data?.DisplaysAndSeat}
            />

            <InspectionSection
              title="Engine & Sub system"
              data={data?.["engine&subSystem"]}
            />

            <div className="bg-white border rounded-lg p-4">
              <h2 className="font-medium text-black mb-3">Vehicle Images</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {data?.vehicleImages?.map((img: any, i: number) => (
                  <div key={i} className="relative group">
                    {/* Image */}
                    <img
                      src={img.ResponseValue}
                      alt={img.Question}
                      onClick={() => {
                        setPreviewImage(img.ResponseValue);
                        setPreviewTitle(img.Question);
                      }}
                      className="w-full h-64 object-cover rounded-md border cursor-pointer hover:opacity-90"
                    />

                    {/* Download Icon */}
                    <a
                      href={img.ResponseValue}
                      download
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 bg-white/80 p-1 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 transition"
                    >
                      ⬇️
                    </a>

                    {/* Label */}
                    <div className="text-sm text-gray-600 mt-2 text-center">
                      {img.Question}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h2 className="font-medium text-black mb-3">Documents</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {data?.customerDocs?.map((img: any, i: number) => (
                  <div key={i} className="relative group">
                    {/* Image */}
                    <img
                      src={img.ResponseValue}
                      alt={img.Question}
                      onClick={() => {
                        setPreviewImage(img.ResponseValue);
                        setPreviewTitle(img.Question);
                      }}
                      className="w-full h-64 object-cover rounded-md border cursor-pointer hover:opacity-90"
                    />

                    {/* Download Icon */}
                    <a
                      href={img.ResponseValue}
                      download
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 bg-white/80 p-1 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 transition"
                    >
                      ⬇️
                    </a>

                    {/* Label */}
                    <div className="text-sm text-gray-600 mt-2 text-center">
                      {img.Question}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Images */}
            {/* <div className="bg-white border rounded-lg p-4">
        <h2 className="font-medium text-black mb-3">Vehicle Images</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data?.vehicleImages?.map((img: any, i: number) => (
            <div key={i}>
              <img
                src={img.ResponseValue}
                alt={img.Question}
                onClick={() => window.open(img.ResponseValue, "_blank")}
                className="w-full h-64 object-cover rounded-md border cursor-pointer hover:opacity-90"
              />

              <div className="text-sm text-gray-600 mt-2 text-center">
                {img.Question}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <h2 className="font-medium text-black mb-3">Documents</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data?.customerDocs?.map((img: any, i: number) => (
            <div key={i}>
              <img
                src={img.ResponseValue}
                alt={img.Question}
                onClick={() => window.open(img.ResponseValue, "_blank")}
                className="w-full h-64 object-cover rounded-md border cursor-pointer hover:opacity-90"
              />

              <div className="text-sm text-gray-600 mt-2 text-center">
                {img.Question}
              </div>
            </div>
          ))}
        </div>
      </div> */}
          </div>
          {previewImage && (
            <div
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
              onClick={() => setPreviewImage(null)}
            >
              <div
                className="bg-white rounded-lg p-3 md:p-4 max-w-4xl w-full mx-3 md:mx-4 relative max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close */}
                <button
                  onClick={() => setPreviewImage(null)}
                  className="absolute top-2 right-2 text-gray-600 text-xl"
                >
                  ✕
                </button>

                {/* Title */}
                <div className="mb-2 font-medium text-center">
                  {previewTitle}
                </div>

                {/* Image */}
                <img
                  src={previewImage ?? ""}
                  alt="preview"
                  className="w-full max-h-[70vh] md:max-h-[80vh] object-contain"
                />

                {/* Download Button */}
                <a
                  href={previewImage || ""}
                  download
                  className="block text-center mt-3 text-blue-600 underline"
                >
                  Download Image
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerInspectionReport;
